import { WHATSAPP_NUMBER } from "../../config/contact";
import { Extra, Place, PricingConfig, PricingData, Route, Tier, Zone } from "./types";

export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  enabled: true,
  whatsappNumber: WHATSAPP_NUMBER,
  currency: "BRL",
  maxPassengers: 6,
  roundTrip: { enabled: true, multiplier: 2 },
  disclaimer: "Valor estimado. A confirmação é feita pelo WhatsApp.",
};

const toArray = <T>(value: unknown): T[] => {
  if (Array.isArray(value)) return value.filter((v) => v !== null && v !== undefined) as T[];
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, T>).filter(
      (v) => v !== null && v !== undefined
    );
  }
  return [];
};

const toNumber = (value: unknown, fallback: number) => {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : fallback;
};

const toRecord = <T>(value: unknown, normalize: (raw: any) => T | null): Record<string, T> => {
  const out: Record<string, T> = {};
  if (!value || typeof value !== "object") return out;
  Object.entries(value as Record<string, unknown>).forEach(([id, raw]) => {
    const normalized = normalize(raw);
    if (normalized) out[id] = normalized;
  });
  return out;
};

export const normalizeTier = (raw: any): Tier | null => {
  if (!raw || typeof raw !== "object") return null;
  return {
    minPax: toNumber(raw.minPax, 1),
    maxPax: toNumber(raw.maxPax, 1),
    price: toNumber(raw.price, 0),
  };
};

export const normalizeConfig = (raw: any): PricingConfig => ({
  enabled: raw?.enabled ?? DEFAULT_PRICING_CONFIG.enabled,
  whatsappNumber:
    typeof raw?.whatsappNumber === "string" && raw.whatsappNumber.replace(/\D/g, "").length >= 10
      ? raw.whatsappNumber.replace(/\D/g, "")
      : DEFAULT_PRICING_CONFIG.whatsappNumber,
  currency: "BRL",
  maxPassengers: Math.max(1, Math.floor(toNumber(raw?.maxPassengers, DEFAULT_PRICING_CONFIG.maxPassengers))),
  roundTrip: {
    enabled: raw?.roundTrip?.enabled ?? DEFAULT_PRICING_CONFIG.roundTrip.enabled,
    multiplier: Math.max(1, toNumber(raw?.roundTrip?.multiplier, DEFAULT_PRICING_CONFIG.roundTrip.multiplier)),
  },
  disclaimer:
    typeof raw?.disclaimer === "string" ? raw.disclaimer : DEFAULT_PRICING_CONFIG.disclaimer,
  updatedAt: typeof raw?.updatedAt === "number" ? raw.updatedAt : undefined,
});

export const normalizeZone = (raw: any): Zone | null => {
  if (!raw || typeof raw.name !== "string") return null;
  return {
    name: raw.name,
    order: toNumber(raw.order, 0),
    active: raw.active !== false,
  };
};

export const normalizePlace = (raw: any): Place | null => {
  if (!raw || typeof raw.name !== "string" || typeof raw.zoneId !== "string") return null;
  return {
    name: raw.name,
    zoneId: raw.zoneId,
    aliases: toArray<string>(raw.aliases).filter((a) => typeof a === "string"),
    active: raw.active !== false,
  };
};

export const normalizeRoute = (raw: any): Route | null => {
  if (!raw || typeof raw.destinationZoneId !== "string") return null;
  return {
    originZoneId: typeof raw.originZoneId === "string" && raw.originZoneId ? raw.originZoneId : "*",
    originPlaceId:
      typeof raw.originPlaceId === "string" && raw.originPlaceId ? raw.originPlaceId : undefined,
    destinationZoneId: raw.destinationZoneId,
    destinationPlaceId:
      typeof raw.destinationPlaceId === "string" && raw.destinationPlaceId
        ? raw.destinationPlaceId
        : undefined,
    bidirectional: raw.bidirectional !== false,
    active: raw.active !== false,
    tiers: toArray<any>(raw.tiers)
      .map(normalizeTier)
      .filter((t): t is Tier => t !== null)
      .sort((a, b) => a.minPax - b.minPax),
  };
};

export const normalizeExtra = (raw: any): Extra | null => {
  if (!raw || typeof raw.name !== "string") return null;
  return {
    name: raw.name,
    price: toNumber(raw.price, 0),
    type: raw.type === "perPax" ? "perPax" : "fixed",
    active: raw.active !== false,
  };
};

/** Converte o snapshot bruto de `pricing/` em dados tipados e seguros. */
export const normalizePricingData = (raw: any): PricingData => ({
  config: normalizeConfig(raw?.config),
  zones: toRecord<Zone>(raw?.zones, normalizeZone),
  places: toRecord<Place>(raw?.places, normalizePlace),
  routes: toRecord<Route>(raw?.routes, normalizeRoute),
  extras: toRecord<Extra>(raw?.extras, normalizeExtra),
});

export const EMPTY_PRICING_DATA: PricingData = {
  config: DEFAULT_PRICING_CONFIG,
  zones: {},
  places: {},
  routes: {},
  extras: {},
};
